"use client";

import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { createContext, type PropsWithChildren, useContext } from "react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { IoCloseOutline, IoMenuOutline } from "react-icons/io5";

const SidebarDrawerContext = createContext<{ onClose?: () => void }>({});
export const useSidebarDrawer = () => useContext(SidebarDrawerContext);

export const SidebarCollapseContext = createContext<{
  isCollapsed: boolean;
  toggleCollapse: () => void;
}>({
  isCollapsed: false,
  toggleCollapse: () => {},
});
export const useSidebarCollapse = () => useContext(SidebarCollapseContext);
export interface SidebarProps extends PropsWithChildren {
  type?: "fixed" | "drawer";
  buttonTheme?: "white" | "dark";
}

export const SidebarFixed = ({ children }: PropsWithChildren) => {
  const { isCollapsed, toggleCollapse } = useSidebarCollapse();
  const sidebarBg = useColorModeValue("white", "navy.800");
  const iconColor = useColorModeValue("gray.600", "gray.300");
  const buttonBg = useColorModeValue("gray.100", "gray.700");
  const buttonBgHover = useColorModeValue("gray.200", "gray.600");
  const borderColor = useColorModeValue("gray.300", "gray.600");

  return (
    <Box minH="100%" position="fixed" display={{ base: "none", xl: "block" }}>
      <Flex
        bg={sidebarBg}
        boxShadow="lg"
        overflow="hidden"
        direction="column"
        borderRadius="8px"
        position="relative"
        ms={{ sm: "16px" }}
        my={{ sm: "16px" }}
        h="calc(100vh - 32px)"
        transition="width 0.2s linear"
        w={isCollapsed ? "80px" : "280px"}
      >
        {children}
        <Box
          left="0"
          h="68px"
          right="0"
          bottom="0"
          zIndex={5}
          bg={sidebarBg}
          position="absolute"
        />
        <Flex
          w="36px"
          h="36px"
          zIndex={10}
          bottom="16px"
          boxShadow="lg"
          bg={buttonBg}
          borderRadius="8px"
          border="1px solid"
          alignItems="center"
          position="absolute"
          justifyContent="center"
          onClick={toggleCollapse}
          borderColor={borderColor}
          transition="all 0.2s linear"
          right={isCollapsed ? "50%" : "16px"}
          transform={isCollapsed ? "translateX(50%)" : "translateX(0)"}
          _hover={{
            bg: buttonBgHover,
            cursor: "pointer",
            transform: isCollapsed
              ? "translateX(50%) scale(1.05)"
              : "translateX(0) scale(1.05)",
          }}
        >
          <Icon
            as={isCollapsed ? HiOutlineChevronRight : HiOutlineChevronLeft}
            boxSize="20px"
            color={iconColor}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export const SidebarDrawer = ({
  buttonTheme = "white",
  children,
}: PropsWithChildren<{ buttonTheme?: "white" | "dark" }>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const iconColor = buttonTheme === "white" ? "background.100" : "gray.400";

  return (
    <Flex display={{ base: "flex", xl: "none" }}>
      <Flex w="max-content" h="max-content" onClick={isOpen ? onClose : onOpen}>
        <Icon
          w="40px"
          h="40px"
          mr="-5px"
          color={iconColor}
          _hover={{ cursor: "pointer" }}
          as={isOpen ? IoCloseOutline : IoMenuOutline}
        />
      </Flex>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay backdropFilter="blur(10px)" />
        <DrawerContent
          w="280px"
          maxW="280px"
          display="flex"
          overflow="hidden"
          ms={{ sm: "16px" }}
          my={{ sm: "16px" }}
          borderRadius="8px"
          flexDirection="column"
        >
          <SidebarDrawerContext.Provider value={{ onClose }}>
            {children}
          </SidebarDrawerContext.Provider>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

export const SidebarWrapper = ({
  children,
  type = "fixed",
  buttonTheme = "white",
}: SidebarProps) => {
  if (type === "drawer") {
    return <SidebarDrawer buttonTheme={buttonTheme}>{children}</SidebarDrawer>;
  }

  return <SidebarFixed>{children}</SidebarFixed>;
};
